/**
 * Capability vocabulary — spec §3.2, §6.7.
 *
 * `payment_accept` is still cut (§0.3): no permission string, no enum member, no
 * code path, and it does not typecheck anywhere.
 *
 * `dm` was cut on the same terms until migration 004 reversed §0.4. It is now a
 * real capability, which moves it from a *vocabulary* question to a *permission*
 * question: an ungranted `dm` is a 403, where it used to be a 400. That is a
 * weaker guarantee by design, so the controls that replace it are enumerated in
 * `DM_CONSTRAINTS` below and enforced in the service layer rather than implied.
 */

/** Capabilities a Listing may grant to a Renter (the `○` rows of §3.2). */
export const GRANTABLE_CAPABILITIES = [
  'view_character',
  'edit_character',
  'run_stop',
  'pause',
  'edit_schedule',
  'generate',
  'post',
  'comment',
  'read_posts',
  'search',
  'upload_asset',
  /**
   * Sending a DM. Default off on every listing — a listing that does not name it
   * does not grant it, like every other `○` capability.
   */
  'dm',
  /**
   * Reading the inbox. Separate from `dm` on purpose: letting a renter read a
   * persona's existing private correspondence is a bigger grant than letting
   * them reply, and an owner should be able to give the second without the first.
   */
  'read_dm',
] as const;

export type GrantableCapability = (typeof GRANTABLE_CAPABILITIES)[number];

/**
 * The controls that carry the abuse weight now that `dm` is a permission rather
 * than an absent word (§12.4). These are constants because every one of them was
 * an argument for cutting DM, and a constant is harder to quietly relax than a
 * default buried in a handler.
 */
export const DM_CONSTRAINTS = {
  /**
   * No cold DMs. A bot may only send into a conversation the counterparty
   * started. This is the single control that separates "answering enquiries"
   * from "solicitation at automated scale", and it is not configurable.
   */
  requireCounterpartyInitiated: true,
  /** Per bot, per day, across all conversations. Owners may set lower. */
  defaultDailyCap: 50,
  /** Per conversation, per day — a cap on pestering one person. */
  defaultPerConversationDailyCap: 10,
  /** Reports at or above this within the window kill DM for the bot outright. */
  reportKillThreshold: 3,
  reportWindowHours: 24,
} as const;

/** Capabilities that are never grantable — owner-only or staff-only. */
export const NON_GRANTABLE_CAPABILITIES = [
  'attach_social_account',
  'list_bot',
  'transfer_bot',
  'archive_bot',
  'approve_rental',
  'suspend_bot',
  'edit_fee_schedule',
  'issue_refund',
  'view_activity',
] as const;

export type Capability =
  | GrantableCapability
  | (typeof NON_GRANTABLE_CAPABILITIES)[number];

export const ALL_CAPABILITIES: readonly Capability[] = [
  ...GRANTABLE_CAPABILITIES,
  ...NON_GRANTABLE_CAPABILITIES,
];

const grantableSet = new Set<string>(GRANTABLE_CAPABILITIES);
const allSet = new Set<string>(ALL_CAPABILITIES);

export function isGrantableCapability(v: string): v is GrantableCapability {
  return grantableSet.has(v);
}

export function isCapability(v: string): v is Capability {
  return allSet.has(v);
}

/**
 * Action types (§4.2 Action). `dm` added by migration 004.
 */
export const ACTION_TYPES = ['post', 'comment', 'reply', 'upload', 'dm'] as const;
export type ActionType = (typeof ACTION_TYPES)[number];

/** Which capability an outbound action type consumes. */
export const ACTION_TYPE_CAPABILITY: Record<ActionType, Capability> = {
  post: 'post',
  comment: 'comment',
  reply: 'comment',
  upload: 'upload_asset',
  dm: 'dm',
};

/**
 * Staff roles. Owner and Renter are deliberately absent: they are per-bot
 * relationships derived from ownership and active rentals (§3.1), never stored
 * as account-level roles.
 */
export const STAFF_ROLES = ['moderator', 'admin', 'support'] as const;
export type StaffRole = (typeof STAFF_ROLES)[number];
